import React from 'react'
import { useParams } from 'react-router'
import { Card } from '@mobi/component-library/Base/Card'
import { BetCard } from '@mobi/betslip/Components/Common/BetCard'
import type { ReplayInfo } from '@core/Areas/Racing/Components/RaceReplay/types'
import type { Tabs, Ticket as TicketType } from '@core/Areas/AccountActivity/types'
import { Details } from './Components/Details/Details'
import { ShareBet } from './Components/ShareBet/ShareBet'
import { getTicketStatus } from './helpers/getTicketStatus'
import { Investment } from './Components/Investment'
import { TicketDisplayContext } from '@core/Areas/AccountActivity/context'
import { RaceReplayVideoPlayer } from './Components/RaceReplayVideoPlayer'
import { RaceReplayButton } from './Components/RaceReplayButton'
import { BetInfo } from './Components/BetInfo'
import { getTicketLayoutType } from './helpers'
import { SuperPick } from './Components/SuperPick'
import { RaceAlertButton } from './Components/RaceAlertButton/RaceAlertButton'

interface Params extends Record<string, string> {
  tab: Tabs
}

export const Ticket: React.FC<{
  ticket: TicketType
}> = ({ ticket }) => {
  const { tab: activeTab } = useParams<Params>() as Params

  const ticketDisplayContext = React.useContext(TicketDisplayContext)

  const [status] = React.useState(() => getTicketStatus(ticket, activeTab))
  const [activeReplayInfo, setActiveReplayInfo] = React.useState<ReplayInfo | null>(null)

  const isPreviewMode = ticketDisplayContext.type === 'share'
  const shouldHideCost = isPreviewMode && ticketDisplayContext.data.shouldHideCost

  const ticketLayoutType = getTicketLayoutType(ticket)
  const expandableText =
    ticketLayoutType === 'multi' || ticketLayoutType === 'same-race-multi'
      ? 'Legs & More'
      : undefined

  const isRaceAbandoned = status === 'Abandoned'

  return (
    <Card data-ticket-seq={ticket.ticketSequence}>
      <BetInfo ticket={ticket} status={status} ticketLayoutType={ticketLayoutType} />

      {!shouldHideCost && <Investment status={status} ticket={ticket} />}

      <SuperPick ticket={ticket} />

      <BetCard.Footer
        expandableText={expandableText}
        expandableContent={<Details ticket={ticket} />}
        rightSection={
          <>
            <ShareBet ticket={ticket} activeTab={activeTab} />

            {activeTab === 'pending' && (
              <RaceAlertButton ticket={ticket} ticketLayoutType={ticketLayoutType} />
            )}

            {!isRaceAbandoned && (
              <RaceReplayButton
                ticket={ticket}
                onPlayClick={info => setActiveReplayInfo(info)}
                onCloseClick={() => setActiveReplayInfo(null)}
                isPlaying={!!activeReplayInfo}
              />
            )}
          </>
        }
      />

      {activeReplayInfo && (
        <RaceReplayVideoPlayer
          ticketId={ticket.ticketSequence}
          raceLocation={activeReplayInfo.raceLocation}
          raceDate={activeReplayInfo.raceDate}
          videoSource={activeReplayInfo.videoSource}
          onCloseTapped={() => setActiveReplayInfo(null)}
          onFinished={() => setActiveReplayInfo(null)}
        />
      )}
    </Card>
  )
}
